import { initializeClickableRowsContacts } from './functions';
import { Modal } from 'bootstrap';
import {
    addTypeableFieldParts,
    charCount,
    coarsePointer,
    initTypeableFormSubmit,
    narrowScreen,
    refreshTypeableField,
    shorten,
    typeableScore,
} from './combobox';

if (document.querySelector(".contacts")) {

    const addGroupEventButton = document.getElementById("add-group-event-button");
    const popupAddEvent = document.getElementById("popupAddEvent");
    const addEventForm = document.getElementById("addEventForm");
    const selectedContactsList = document.getElementById("selectedContactsList");
    const noContactsSelected = document.getElementById("noContactsSelected");

    if (addGroupEventButton) {
        addGroupEventButton.addEventListener("click", function () {
            let selectedContacts = document.querySelectorAll(".contact-checkbox:checked");

            if (selectedContacts.length === 0) {
                alert("❌ Please select at least one contact before adding an event.");
                return;
            }

            // One Modal instance per element: `new Modal()` replaced the shown instance, so
            // the later hide() (and Cancel / X) acted on an instance that was never shown.
            Modal.getOrCreateInstance(popupAddEvent).show();

            document.body.classList.add("blurred");

            selectedContactsList.innerHTML = "";
            noContactsSelected.style.display = "none";

            selectedContacts.forEach(contact => {
                let contactID = contact.value;
                let listItem = document.createElement("li");
                listItem.classList.add("list-group-item", "contacts-selected__chip");
                // Presentational: show the company/name (from data-contact-label) next to the ID.
                const contactLabel = contact.dataset ? contact.dataset.contactLabel : '';
                listItem.textContent = contactLabel ? `${contactLabel} · #${contactID}` : `ID: ${contactID}`;
                listItem.title = listItem.textContent;
                listItem.dataset.contactId = contactID;
                selectedContactsList.appendChild(listItem);
            });
        });
    }

    const submitCreateBulkLeads = document.getElementById("submit-create_bulk-leads");

    if (addEventForm && submitCreateBulkLeads) {
        addEventForm.addEventListener("submit", function (event) {
            event.preventDefault();

            let selectedContacts = [...document.querySelectorAll(".contact-checkbox:checked")].map(cb => cb.value);

            if (selectedContacts.length === 0) {
                alert("❌ No contacts selected.");
                return;
            }

            let jsonData = {
                ...Object.fromEntries(new FormData(addEventForm).entries()),
                contactIds: selectedContacts
            };

            axios.post("/leads/create-bulk", jsonData, {
                headers: {
                    "Content-Type": "application/json"
                }
            })
                .then(response => {
                    const data = response.data;
                    if (data.success) {
                        alert("✅ Group event added successfully!");
                        Modal.getOrCreateInstance(popupAddEvent).hide();
                    } else {
                        alert("❌ Error: " + data.error);
                    }
                })
                .catch(error => {
                    console.error("❌ Error submitting group event:", error);
                    alert("⚠️ Error: " + (error.response?.data?.message || error.message));
                });
        });
    }

    initializeClickableRowsContacts();

    const multipleEmailLink = document.getElementById("multiple-email-link");
    // The link has no href until a click builds the mailto:, so Enter/Space would do
    // nothing: activate it like a button (it carries role="button").
    multipleEmailLink.addEventListener("keydown", function (event) {
        if ((event.key === "Enter" || event.key === " ") && this.getAttribute("aria-disabled") !== "true") {
            event.preventDefault();
            this.click();
        }
    });
    multipleEmailLink.addEventListener("click", function (event) {
        if (this.getAttribute("aria-disabled") === "true") {
            event.preventDefault();
            return;
        }

        let selectedContacts = document.querySelectorAll(".contact-checkbox:checked");
        let emails = '';
        selectedContacts.forEach(contact => {
            const parentRow = contact.closest('tr');
            const emailCell = parentRow.querySelector('td a[href^="mailto:"]');
            if (emailCell) {
                const email = emailCell.href.replace('mailto:', '');
                emails += email + ',';
            } else {
                console.error("No email found in parent row.");
            }
        });

        if (emails) {
            const emailLink = document.getElementById('multiple-email-link');

            emailLink.href = `mailto:${emails}`;
        } else {
            alert("❌ No emails found for selected contacts.");
        }
    });
}

// ---- Contact create / edit: the company type field ----------------------------------------
// <select class="contact-type-select" name="type"> (form.blade.php) becomes a typeable combobox
// (combobox.js, the same field as the order form's product): a click or focus lists every type
// (the predefined ones, then the custom types used on contacts), typing filters them, and text
// that names no type is confirmed with the 'Use "<text>" as a new type' row. Typing a type's
// name in any case is that type ("feed" is Feed). Submitted: a predefined type's slug or
// CUSTOM_TYPE_PREFIX + a custom type's text (a type's text is never a raw TomSelect option key:
// options live in a plain object, where "__proto__" or "hasOwnProperty" broke the field), which
// CreateContactRequest strips and normalises again. Leaving the field never picks or creates a
// type: text left in it that names no type (also typed over a type: fx_typeable
// pendingOverValue) stays, marked as not chosen yet, and blocks the save with an inline message
// (Escape brings the previous type back).
const CONTACT_TYPE_MAX = 50; // contacts.type string(50) (CreateContactRequest max; characters)
const CUSTOM_TYPE_PREFIX = 'custom:'; // Contact::CUSTOM_TYPE_PREFIX
const CONTACT_TYPE_PLACEHOLDER = 'Type to search or add a type';
const CONTACT_TYPE_PLACEHOLDER_SHORT = 'Search or add a type';
const CONTACT_TYPE_REQUIRED_MESSAGE = 'Choose a company type or type a new one.';
const CONTACT_TYPE_TOO_LONG_MESSAGE = `A company type can be at most ${CONTACT_TYPE_MAX} characters.`;
const CONTACT_TYPE_NEW_BADGE = '<span class="fx-combobox__badge" title="Saved as a new type with this contact">New</span>';

// As the server stores it: trimmed, inner runs of spaces collapsed
function cleanContactType(text) {
    return String(text ?? '').replace(/\s+/g, ' ').trim();
}

function contactTypeKey(text) {
    return cleanContactType(text).toLowerCase();
}

// The type that typed text names, ignoring case and repeated spaces: by its name, or a
// predefined type by its stored value ("ginner_and_producer")
function findContactType(tomSelect, text) {
    const key = contactTypeKey(text);
    if (!key) {
        return null;
    }

    const options = Object.values(tomSelect.options);
    return options.find(option => contactTypeKey(option.text) === key)
        || options.find(option => option.predefined && String(option.value).toLowerCase() === key)
        || null;
}

// What the field still needs ('' when it holds a type)
function contactTypeProblem(tomSelect) {
    const value = tomSelect.getValue();
    if (value) {
        // a type typed before a failed save comes back as it was sent (the server says why)
        const option = tomSelect.options[value];
        return option && charCount(cleanContactType(option.text)) > CONTACT_TYPE_MAX ? CONTACT_TYPE_TOO_LONG_MESSAGE : '';
    }
    const text = cleanContactType(tomSelect.inputValue());
    if (charCount(text) > CONTACT_TYPE_MAX) {
        return CONTACT_TYPE_TOO_LONG_MESSAGE;
    }
    if (text !== '') {
        return `Pick a type from the list, or add "${shorten(text)}" as a new type.`;
    }
    return CONTACT_TYPE_REQUIRED_MESSAGE;
}

function refreshContactTypeField(tomSelect, fromSaveAttempt = false) {
    const value = tomSelect.getValue();
    const option = value ? tomSelect.options[value] : null;

    refreshTypeableField(tomSelect, {
        problem: contactTypeProblem(tomSelect),
        fromSaveAttempt,
        errorClass: 'text-danger', // the contact form's validation text
        describe(invalid) {
            // Help line: what a new type does on save, or text that is not chosen yet
            if (!invalid && option && option.isNew) {
                return {
                    help: 'New type: saved with this contact and suggested for other contacts.',
                    value: `New company type: ${option.text}`,
                };
            }
            const text = cleanContactType(tomSelect.inputValue());
            if (!invalid && !value && tomSelect.fxPending && text !== '') {
                // text typed over a type: Escape (in the field) brings that type back
                const last = tomSelect.fxLastValue ? tomSelect.options[tomSelect.fxLastValue] : null;
                let help = `Not chosen yet: pick a type from the list, or add "${shorten(text)}" as a new type.`;
                if (charCount(text) > CONTACT_TYPE_MAX) {
                    help = CONTACT_TYPE_TOO_LONG_MESSAGE;
                } else if (last && coarsePointer.matches) {
                    help = `Not chosen yet (was ${shorten(last.text)}): pick a type from the list, or add "${shorten(text)}" as a new type.`;
                } else if (last) {
                    help = `Not chosen yet: pick a type from the list, add "${shorten(text)}" as a new type, or press Esc in the field to keep ${shorten(last.text)}.`;
                }
                return { help, tone: 'warn' };
            }
            return { value: option ? `Company type: ${option.text}` : '' };
        },
    });
}

function contactTypeChanged(tomSelect, value) {
    const option = value ? tomSelect.options[value] : null;
    const via = tomSelect.fxSelectVia;

    // A new type typed earlier and replaced since is no longer offered
    Object.values(tomSelect.options).forEach(other => {
        if (other.isNew && other.value !== value && other.value !== tomSelect.fxLastValue) {
            tomSelect.removeOption(other.value);
        }
    });

    refreshContactTypeField(tomSelect);

    // On a touch screen a tapped type closes the keyboard (as the product field). As soon as
    // TomSelect has finished handling the tap (a microtask)
    if (option && via === 'pointer' && coarsePointer.matches) {
        queueMicrotask(() => {
            if (tomSelect.isFocused) {
                tomSelect.blur();
            }
        });
    }
}

// In the full list: a heading over the first custom type (after the predefined ones)
function markCustomTypes(tomSelect) {
    const content = tomSelect.dropdown_content;
    if (tomSelect.inputValue() !== '' || content.querySelector(':scope > .fx-combobox__heading--group')) {
        return;
    }
    const customRows = [...content.querySelectorAll(':scope > [data-selectable]')]
        .filter(row => String(row.dataset.value || '').startsWith(CUSTOM_TYPE_PREFIX));
    // (not for a new type typed here alone: it is not on any contact yet)
    if (!customRows.some(row => !(tomSelect.options[row.dataset.value] || {}).isNew)) {
        return;
    }
    const firstCustom = customRows[0];
    const previous = firstCustom.previousElementSibling;
    if (previous && previous.matches('[data-selectable]')) {
        const groupHeading = document.createElement('div');
        groupHeading.className = 'fx-combobox__heading fx-combobox__heading--group';
        groupHeading.setAttribute('role', 'presentation');
        groupHeading.textContent = 'Added by your team';
        firstCustom.before(groupHeading);
    }
}

function initContactTypeField(select) {
    if (!select) {
        return null;
    }
    if (select.tomselect) {
        return select.tomselect;
    }

    // The options are the <select>'s own (TomSelect reads data-predefined / data-is-new)
    const tomSelect = new TomSelect(select, {
        plugins: [
            {
                name: 'fx_typeable',
                options: {
                    // how many types there are: the list shows only the first few
                    heading: field => {
                        const count = Object.values(field.options).filter(option => !option.isNew).length;
                        return `All ${count} types · type to filter`;
                    },
                    findOption: findContactType,
                    browseFilled: true,
                    chevron: true,          // a text field that is also a dropdown: it shows
                    pendingOverValue: true, // a required field: typed-over text is never dropped
                },
            },
            'restore_on_backspace',
        ],
        placeholder: narrowScreen.matches ? CONTACT_TYPE_PLACEHOLDER_SHORT : CONTACT_TYPE_PLACEHOLDER,
        hidePlaceholder: true,
        openOnFocus: false,       // fx_typeable lists the types on focus / click
        refreshThrottle: 0,       // local list: refresh on every keystroke
        maxOptions: 1000,
        searchField: ['text'],
        // Match anywhere in the name, ignoring case and accents (combobox.js typeableScore); a
        // predefined type's stored value typed as is ("ginner_and_producer") also finds it
        diacritics: false,
        score(search) {
            const byName = typeableScore(search);
            const key = contactTypeKey(search);
            return option => (option.predefined && String(option.value).toLowerCase() === key ? 3 : byName(option));
        },
        sortField: [{ field: '$score', direction: 'desc' }, { field: '$order' }],
        controlInput: '<input type="text" autocomplete="off" autocorrect="off" spellcheck="false" enterkeyhint="done" size="1">',
        create(input) {
            const text = cleanContactType(input);
            return { value: CUSTOM_TYPE_PREFIX + text, text, isNew: true };
        },
        createFilter(input) {
            const text = cleanContactType(input);
            return text !== '' && charCount(text) <= CONTACT_TYPE_MAX && !findContactType(this, text);
        },
        render: {
            option(data, escape) {
                return `<div class="fx-combobox__option"><span class="fx-combobox__label">${escape(data.text)}</span>${data.isNew ? CONTACT_TYPE_NEW_BADGE : ''}</div>`;
            },
            item(data, escape) {
                return `<div><span class="fx-combobox__label">${escape(data.text)}</span>${data.isNew ? CONTACT_TYPE_NEW_BADGE : ''}</div>`;
            },
            option_create(data, escape) {
                return `<div class="create fx-combobox__create" role="option" id="${escape(this.inputId)}-ts-create">`
                    + '<i class="fa-solid fa-plus" aria-hidden="true"></i>'
                    + `<span>Use "<strong>${escape(cleanContactType(data.input))}</strong>" as a new type`
                    + '<small>Saved with this contact and suggested for other contacts</small></span></div>';
            },
            no_results(data, escape) {
                const message = charCount(cleanContactType(data.input)) > CONTACT_TYPE_MAX
                    ? `A company type can be at most ${CONTACT_TYPE_MAX} characters`
                    : 'No matching types';
                return `<div class="no-results">${escape(message)}</div>`;
            },
        },
        onChange(value) {
            contactTypeChanged(this, value);
        },
    });

    // Typed text highlights the type it names (any case: Enter / Tab take it), else the best
    // match; not the current type just because it also matches (TomSelect's default for a
    // single field), nor a lower-ranked exact name ("pot" -> a custom "Pot", not Potatoes).
    // Once per text, so a row reached with the arrow keys stays highlighted.
    // The full list (no text) marks where the types added on contacts start.
    let highlightedFor = '';
    tomSelect.hook('after', 'refreshOptions', () => {
        const text = tomSelect.inputValue();
        if (text === '' || !tomSelect.isOpen) {
            highlightedFor = '';
            markCustomTypes(tomSelect);
            return;
        }
        if (text === highlightedFor) {
            return;
        }
        highlightedFor = text;
        const exact = findContactType(tomSelect, text);
        const exactRow = exact ? tomSelect.getOption(exact[tomSelect.settings.valueField]) : null;
        const target = exactRow && tomSelect.dropdown_content.contains(exactRow) ? exactRow : tomSelect.selectable()[0];
        if (target && target !== tomSelect.activeOption) {
            tomSelect.setActiveOption(target);
        }
    });

    // Accessibility: required, and the field's value / help / error read with it. A type error
    // from the server (failed save) is shown until the field holds a type.
    if (select.required) {
        tomSelect.control_input.setAttribute('aria-required', 'true');
    }
    addTypeableFieldParts(tomSelect, select.parentElement.querySelector(':scope > .contact-type-error'));

    tomSelect.on('type', () => refreshContactTypeField(tomSelect));
    tomSelect.on('blur', () => refreshContactTypeField(tomSelect));
    tomSelect.on('focus', () => refreshContactTypeField(tomSelect));
    tomSelect.on('fx_pending', () => refreshContactTypeField(tomSelect));
    tomSelect.on('fx_text', () => refreshContactTypeField(tomSelect));
    select.addEventListener('invalid', () => {
        tomSelect.wrapper.classList.add('fx-combobox--invalid');
        refreshContactTypeField(tomSelect, true);
    });

    refreshContactTypeField(tomSelect);

    return tomSelect;
}

if (document.querySelector(".contacts-create, .contacts-edit")) {
    const contactTypeSelect = document.querySelector('select.contact-type-select');
    if (contactTypeSelect && window.TomSelect) {
        initContactTypeField(contactTypeSelect);
        // Saving: a type field that blocks the save gets an inline message and the focus (not
        // the browser bubble); a valid save is sent once
        initTypeableFormSubmit(contactTypeSelect.form, 'select.contact-type-select');
    }
}
